import { useRequiredContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RegistrationRewardType } from "@redotech/redo-model/extended-warranty";
import {
  GeneralPermissions,
  SettingsPermissions,
} from "@redotech/redo-model/permissions";
import {
  SettingItem,
  SettingsSection,
  SettingsSectionEnum,
} from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { filterTruthy } from "@redotech/util/array";
import { useContext } from "react";
import { ConversionSuiteStatusesContext } from "../../../redo-suite/conversion-cloud";
import { MarketingCloudStatusesContext } from "../../../redo-suite/marketing-cloud";
import { OrderManagementSuiteStatusesContext } from "../../../redo-suite/order-management-cloud";
import { SupportSuiteStatusesContext } from "../../../redo-suite/support-cloud";

export function useSettingsItems(): SettingsSection[] | null {
  const generalSettings = useGeneralSettings();
  const personalSettings = usePersonalSettings();
  const aiSettings = useAiSettings();
  const conversionSettings = useConversionSettings();
  const customersSettings = useCustomersSettings();
  const marketingSettings = useMarketingSettings();
  const productsSettings = useProductsSettings();
  const returnsSettings = useReturnsSettings();
  const warrantiesSettings = useWarrantiesSettings();
  const fulfillmentSettings = useFulfillmentSettings();
  const shippingSettings = useShippingSettings();
  const supportSettings = useSupportSettings();

  const allSettingsItems: SettingsSection[] = filterTruthy([
    generalSettings,
    personalSettings,
    aiSettings,
    conversionSettings,
    customersSettings,
    marketingSettings,
    returnsSettings,
    productsSettings,
    warrantiesSettings,
    fulfillmentSettings,
    shippingSettings,
    supportSettings,
  ]);

  return allSettingsItems;
}

function useAiSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { emailSmsEnabled } = useRequiredContext(MarketingCloudStatusesContext);
  const canManageAiSettings = useHasPermission(
    SettingsPermissions.SET_AI_SETTINGS,
  );

  if (!canManageAiSettings) {
    return undefined;
  }

  const items: SettingItem[] = filterTruthy([
    {
      id: "ai-general",
      href: routes.getSettingsSubRoute("ai/general"),
      friendlyName: "General",
    },
    {
      id: "ai-knowledge",
      href: routes.getSettingsSubRoute("ai/knowledge"),
      friendlyName: "Knowledge",
    },
    {
      id: "ai-skills",
      href: routes.getSettingsSubRoute("ai/skills"),
      friendlyName: "Skills",
    },
    emailSmsEnabled && {
      id: "ai-context-library",
      href: routes.getSettingsSubRoute("ai/ai-context-library"),
      friendlyName: "Context Library",
    },
  ]);

  return { name: SettingsSectionEnum.AI, items };
}

function useConversionSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { tnplEnabled } = useRequiredContext(MarketingCloudStatusesContext);
  const { checkoutOptimizationEnabled } = useRequiredContext(
    ConversionSuiteStatusesContext,
  );

  const canManageTNPLSettings = useHasPermission(
    SettingsPermissions.SET_TNPL_SETTINGS,
  );

  const canManageCheckoutOptimizationSettings = useHasPermission(
    SettingsPermissions.SET_CHECKOUT_OPTIMIZATION_SETTINGS,
  );

  const items: SettingItem[] = filterTruthy([
    tnplEnabled &&
      canManageTNPLSettings && {
        id: "cart-optimization",
        href: routes.getSettingsSubRoute(
          "conversion/try-first/cart-optimization",
        ),
        friendlyName: "Cart optimization",
      },
    checkoutOptimizationEnabled &&
      canManageCheckoutOptimizationSettings && {
        id: "general",
        href: routes.getSettingsSubRoute("conversion/general"),
        friendlyName: "General",
      },
    tnplEnabled &&
      canManageTNPLSettings && {
        id: "try-first",
        href: routes.getSettingsSubRoute("conversion/try-first"),
        friendlyName: "Try first",
      },
  ]);

  // Only show the section if at least one page is accessible
  if (items.length === 0) {
    return undefined;
  }

  items.sort((a, b) => {
    // sort by friendlyName but make sure "General" is at the top
    if (a.friendlyName === "General") {
      return -1;
    }

    if (b.friendlyName === "General") {
      return 1;
    }

    return a.friendlyName.localeCompare(b.friendlyName);
  });

  return { name: SettingsSectionEnum.CONVERSION, items };
}

function useCustomersSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  // Note: customerAccounts is not part of our centralized suites yet, keeping team access
  const team = useRequiredContext(TeamContext);
  const customerAccountsEnabled = team.settings.customerAccounts?.enabled;
  const canManageCustomersSettings = useHasPermission(
    SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
  );

  if (!customerAccountsEnabled || !canManageCustomersSettings) {
    return undefined;
  }

  const items: SettingItem[] = [
    {
      id: "customer-accounts-ad-blocks",
      href: routes.getSettingsSubRoute("customer-accounts-ad-blocks"),
      friendlyName: "Ad blocks",
    },
    {
      id: "customer-accounts-integrations",
      href: routes.getSettingsSubRoute("customer-accounts-integrations"),
      friendlyName: "Integrations",
    },
    {
      id: "customer-accounts-storefront-display",
      href: routes.getSettingsSubRoute("customer-accounts-storefront-display"),
      friendlyName: "Storefront display",
    },
  ];

  return { name: SettingsSectionEnum.CUSTOMER_ACCOUNTS, items };
}

function useFulfillmentSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const canManageFulfillmentSettings = useHasPermission(
    SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  );

  const canManageAdminGeneralSettings = useHasPermission(
    SettingsPermissions.SET_GENERAL_SETTINGS,
  );

  if (
    !(omsEnabled && canManageFulfillmentSettings) &&
    !canManageAdminGeneralSettings
  ) {
    return undefined;
  }

  const items: SettingItem[] = filterTruthy([
    ...(omsEnabled && canManageFulfillmentSettings
      ? [
          {
            id: "general-oms",
            href: routes.getSettingsSubRoute("oms/general"),
            friendlyName: "General",
          },
          {
            id: "automerge-link",
            href: routes.getSettingsSubRoute("oms/automerge-shipments"),
            friendlyName: "Auto-merge shipments",
          },
          {
            id: "automations-oms",
            href: routes.getSettingsSubRoute("oms/automations"),
            friendlyName: "Automations",
          },
          {
            id: "balance-link",
            href: routes.getSettingsSubRoute("oms/balance"),
            friendlyName: "Balance",
          },
          {
            id: "integrations",
            href: routes.getSettingsSubRoute("oms/integrations"),
            friendlyName: "Integrations",
          },
          {
            id: "international",
            href: routes.getSettingsSubRoute("oms/international"),
            friendlyName: "International",
          },
        ]
      : []),
    canManageAdminGeneralSettings && {
      id: "order-editing-button",
      href: routes.getSettingsSubRoute("order-management/order-editing"),
      friendlyName: "Order editing",
    },
    ...(omsEnabled && canManageFulfillmentSettings
      ? [
          {
            id: "parcels-link",
            href: routes.getSettingsSubRoute("oms/packages"),
            friendlyName: "Packages",
          },
          {
            id: "presets-link",
            href: routes.getSettingsSubRoute("oms/presets"),
            friendlyName: "Presets",
          },
          {
            id: "reports",
            href: routes.getSettingsSubRoute("oms/reports"),
            friendlyName: "Reports",
          },
          {
            id: "scanning-warehouse",
            href: routes.getSettingsSubRoute("oms/scanning-warehouse"),
            friendlyName: "Scanning & warehouse",
          },
          {
            id: "security",
            href: routes.getSettingsSubRoute("oms/security"),
            friendlyName: "Security",
          },
          {
            id: "warehouse-layouts",
            href: routes.getSettingsSubRoute("oms/warehouse-layouts"),
            friendlyName: "Warehouse layouts",
          },
        ]
      : []),
  ]);

  return { name: SettingsSectionEnum.ORDER_FULFILLMENT, items };
}

function usePersonalSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);

  const items: SettingItem[] = [
    {
      id: "personal-profile",
      href: routes.getSettingsSubRoute("personal"),
      friendlyName: "Profile",
    },
  ];
  return { name: SettingsSectionEnum.PERSONAL, items };
}

function useGeneralSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);
  // Note: Some settings not in centralized suites yet, keeping team access
  const team = useRequiredContext(TeamContext);
  const aiEnabled = team.settings.support?.ai?.enabled;
  const customerAccountsEnabled = team.settings.customerAccounts?.enabled;

  const canManageAdminGeneralSettings = useHasPermission(
    SettingsPermissions.SET_GENERAL_SETTINGS,
  );

  const canManageSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canManageAiSettings = useHasPermission(
    SettingsPermissions.SET_AI_SETTINGS,
  );
  const canManageCustomerAccountsSettings = useHasPermission(
    SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
  );

  const canManageTeamRoles = useHasPermission(
    GeneralPermissions.MANAGE_TEAM_ROLES,
  );

  const canManageChatWidget =
    canManageSupportSettings ||
    canManageAiSettings ||
    canManageCustomerAccountsSettings ||
    canManageAdminGeneralSettings;
  const chatWidgetEnabled =
    supportEnabled || aiEnabled || customerAccountsEnabled;
  const showChatWidget = chatWidgetEnabled && canManageChatWidget;

  const canManageDeveloperSettings = useHasPermission(
    GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
  );

  const canManageTeamUsers = useHasPermission(
    GeneralPermissions.MANAGE_TEAM_USERS,
  );

  const items: SettingItem[] = filterTruthy([
    canManageAdminGeneralSettings && {
      id: "billing-button",
      href: routes.getSettingsSubRoute("billing"),
      friendlyName: "Billing",
    },
    canManageAdminGeneralSettings && {
      id: "brand-kit-button",
      href: routes.getSettingsSubRoute("brand-kit"),
      friendlyName: "Brand kit",
    },
    canManageAdminGeneralSettings && {
      id: "communication-button",
      href: routes.getSettingsSubRoute("communication"),
      friendlyName: "Communication",
    },
    canManageAdminGeneralSettings && {
      id: "customer-portal-button",
      href: routes.getSettingsSubRoute("customer-portal"),
      friendlyName: "Customer portal",
    },
    showChatWidget && {
      id: "customer-widget-button",
      href: routes.getSettingsSubRoute("customer-widget"),
      friendlyName: "Customer widget",
    },
    canManageDeveloperSettings && {
      id: "developer-button",
      href: routes.getSettingsSubRoute("developer"),
      friendlyName: "Developer",
    },
    canManageAdminGeneralSettings && {
      id: "invoices-button",
      href: routes.getSettingsSubRoute("invoices"),
      friendlyName: "Invoices",
    },
    canManageAdminGeneralSettings && {
      id: "notifications-button",
      href: routes.getSettingsSubRoute("notifications"),
      friendlyName: "Notifications",
    },
    canManageAdminGeneralSettings && {
      id: "theme-button",
      href: routes.getSettingsSubRoute("theme"),
      friendlyName: "Theme",
    },
    canManageTeamRoles && {
      id: "roles-button",
      href: routes.getSettingsSubRoute("roles"),
      friendlyName: "Roles",
    },
    canManageTeamUsers && {
      id: "users-button",
      href: routes.getSettingsSubRoute("users"),
      friendlyName: "Users",
    },
  ]);

  return { name: SettingsSectionEnum.GENERAL, items };
}

function useMarketingSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { returnTrackingEnabled, emailSmsEnabled, orderTrackingEnabled } =
    useRequiredContext(MarketingCloudStatusesContext);
  const canManageMarketingSettings = useHasPermission(
    SettingsPermissions.SET_MARKETING_SETTINGS,
  );

  if (
    (!returnTrackingEnabled && !emailSmsEnabled && !orderTrackingEnabled) ||
    !canManageMarketingSettings
  ) {
    return undefined;
  }

  const items: SettingItem[] = [
    {
      id: "audience",
      href: routes.getSettingsSubRoute("audience"),
      friendlyName: "Audience",
    },
    {
      id: "email-domain",
      href: routes.getSettingsSubRoute("email-domain"),
      friendlyName: "Email domain",
    },
    {
      id: "marketing-sms",
      href: routes.getSettingsSubRoute("sms-marketing"),
      friendlyName: "SMS",
    },
    {
      id: "marketing-integrations",
      href: routes.getSettingsSubRoute("marketing-integrations"),
      friendlyName: "Integrations",
    },
  ];

  return { name: SettingsSectionEnum.MARKETING, items };
}

function useProductsSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const canManageProductsSettings = useHasPermission(
    SettingsPermissions.SET_GENERAL_SETTINGS,
  );
  if (!canManageProductsSettings) {
    return;
  }
  return {
    name: SettingsSectionEnum.PRODUCTS,
    items: [
      {
        id: "products-button",
        href: routes.getSettingsSubRoute("products"),
        friendlyName: "Products",
      },
    ],
  };
}

function useReturnsSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);

  const team = useRequiredContext(TeamContext);

  const canManageRoles = useHasPermission(GeneralPermissions.MANAGE_TEAM_ROLES);

  const bundlesEnabled =
    team.platform === "shopify" || team.platform === "bigcommerce";

  const canManageReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );

  if (!canManageReturnsSettings) {
    return undefined;
  }

  const items: SettingItem[] = filterTruthy([
    {
      id: "assessments",
      href: routes.getSettingsSubRoute("assessments"),
      friendlyName: "Assessments",
    },
    {
      id: "automations-button",
      href: routes.getSettingsSubRoute("automations"),
      friendlyName: "Automations",
    },
    bundlesEnabled && {
      id: "bundles",
      href: routes.getSettingsSubRoute("bundles"),
      friendlyName: "Bundles",
    },
    {
      id: "coverage-button",
      href: routes.getSettingsSubRoute("coverage"),
      friendlyName: "Coverage",
    },
    {
      id: "exchanges-button",
      href: routes.getSettingsSubRoute("exchanges"),
      friendlyName: "Exchanges",
    },
    {
      id: "flows",
      href: routes.getSettingsSubRoute("flows"),
      friendlyName: "Flows",
    },
    {
      id: "info-modal",
      href: routes.getSettingsSubRoute("info-modal"),
      friendlyName: "Info Modal",
    },
    {
      id: "integrations",
      href: routes.getSettingsSubRoute("integrations"),
      friendlyName: "Integrations",
    },
    canManageRoles && {
      id: "permissions",
      href: routes.getSettingsSubRoute("permissions"),
      friendlyName: "Permissions",
    },
    {
      id: "point-of-sale",
      href: routes.getSettingsSubRoute("pos"),
      friendlyName: "Point of sale",
    },
    {
      id: "processing-button",
      href: routes.getSettingsSubRoute("processing"),
      friendlyName: "Processing",
    },
    {
      id: "shipping-button",
      href: routes.getSettingsSubRoute("shipping"),
      friendlyName: "Shipping",
    },
    {
      id: "returns-ui",
      href: routes.getSettingsSubRoute("returns-ui"),
      friendlyName: "UI",
    },
  ]);

  return { name: SettingsSectionEnum.RETURNS_AND_CLAIMS, items };
}

function useWarrantiesSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { warrantiesEnabled } = useContext(OrderManagementSuiteStatusesContext);

  const team = useRequiredContext(TeamContext);
  const registrationRewardsEnabled =
    team.settings.warranties?.registrationRewardType !==
    RegistrationRewardType.DISABLED;

  const canManageWarrantiesSettings = useHasPermission(
    SettingsPermissions.SET_WARRANTY_SETTINGS,
  );

  if (!warrantiesEnabled || !canManageWarrantiesSettings) {
    return undefined;
  }

  const items: SettingItem[] = filterTruthy([
    registrationRewardsEnabled && {
      id: "warranty-registration-button",
      href: routes.getSettingsSubRoute("registration-rewards"),
      friendlyName: "Registration rewards",
    },
  ]);

  return { name: SettingsSectionEnum.WARRANTIES, items };
}

function useShippingSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  const canManageShippingSettings = useHasPermission(
    SettingsPermissions.SET_SHIPPING_SETTINGS,
  );

  if (!canManageShippingSettings) {
    return undefined;
  }

  // cspell:disable-next-line
  // Third party TESCOM teams should not have access to shipping settings
  const hideTeams = [
    "68a50f1378bdf9a9c1352e8b",
    "68a51f39ad07ff22f074fb25",
    "68a51f7715aa9428462967c6",
    "68a52001e14afead23f253cd",
    "68a51fbf1aa2c4f5c2b6c0c3",
  ];

  const hideShippingSettings = team?._id && hideTeams.includes(team?._id);
  if (hideShippingSettings) {
    return undefined;
  }

  const items: SettingItem[] = [
    {
      id: "carriers-link",
      href: routes.getSettingsSubRoute("carriers"),
      friendlyName: "Carriers",
    },
    {
      id: "fulfillment-cutoff-link",
      href: routes.getSettingsSubRoute("fulfillment-cutoff"),
      friendlyName: "Fulfillment promise",
    },
  ];

  return { name: SettingsSectionEnum.SHIPPING, items };
}

function useSupportSettings(): SettingsSection | undefined {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { supportEnabled } = useRequiredContext(SupportSuiteStatusesContext);

  const canManageSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );

  if (!supportEnabled || !canManageSupportSettings) {
    return undefined;
  }

  const items: SettingItem[] = [
    {
      id: "support-general",
      href: routes.getSettingsSubRoute("general"),
      friendlyName: "General",
    },
    {
      id: "support-channels",
      href: routes.getSettingsSubRoute("channels"),
      friendlyName: "Integrations",
    },
    {
      id: "support-customer-satisfaction",
      href: routes.getSettingsSubRoute("customer-satisfaction"),
      friendlyName: "Customer satisfaction",
    },
    {
      id: "support-tags",
      href: routes.getSettingsSubRoute(`${"tags"}`),
      friendlyName: "Tags",
    },
    {
      id: "support-faq",
      href: routes.getSettingsSubRoute("faq"),
      friendlyName: "FAQs",
    },
    {
      id: "support-rules",
      href: routes.getSettingsSubRoute("rules"),
      friendlyName: "Rules",
    },
  ];

  return { name: SettingsSectionEnum.SUPPORT, items };
}
